<template>
  <div id="app">
    <div class="spinner bg-white" v-if="showLoader">
      <div>
        <b-spinner variant="main" class="loader" type="grow" label="Spinning"></b-spinner>
      </div>
    </div>
    <b-container fluid>
      <Header />
      <b-row id="wrapper" :class="{ toggled: isMobile ? !sidebarCollapsed : openSidebar, 'geotab-sidebar-wrapper':isGeotabAddin, collapsed: !isMobile && sidebarCollapsed }">
        <Sidebar ref="sidebar" v-on:sidebarState="getState($event)" v-if="!showLoader" :expiryWarning="expiryWarning"
          :getStripeStatus="getStripeStatus" :getEnterpriseStatus="getEnterpriseStatus"/>
        <div id="page-content-wrapper" class="table-body">
          <router-view v-if="user && company && company[0] && userCompany && rolePermissions" :key="$route.fullPath" />
        </div>
      </b-row>
    </b-container>
    <AccountExpiry v-if="shouldShowAccountExpiry" />
    <WelcomeOwnerOperator/>
  </div>
</template>

<script>
import Header from "@/components/header/Header.vue";
import Sidebar from "@/components/sidebar/Sidebar.vue";
import { mapActions, mapGetters } from "vuex";
import * as moment from "moment-timezone";
import AccountExpiry from "@/components/AccountExpiry"
import WelcomeOwnerOperator from "@/components/WelcomeOwnerOperator";
import { initGleapWithUser } from "../plugins/gleap";
import { getGeotabSession } from "@/services/geotab";
export default {
  name: "Website",
  components: {
    Header,
    Sidebar,
    AccountExpiry,
    WelcomeOwnerOperator
  },
  data() {
    return {
      openSidebar: false,
      expiryWarning: "",
      showAccountExpiry: false,
      showLoader: false,
      isMobile: window.innerWidth <= 767,
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  computed: {
    ...mapGetters(["user", "licenseInventory", 'company', 'rolePermissions', 'userCompany', 'isGeotabAddin', 'sidebarCollapsed']),
    shouldShowAccountExpiry() {
      return ((this.showAccountExpiry && this.company[0]?.enterprise === true) || (this.company[0]?.enterprise === false && this.company[0]?.subscription?.is_in_trial === false && (this.company[0]?.subscription?.type === 'Stripe' && this.company[0]?.subscription?.status === 'inactive')))
    },
    getStripeStatus() {
      return (this.company[0]?.subscription?.type !== 'Stripe') || (this.company[0]?.subscription?.type === 'Stripe' && this.company[0]?.subscription?.is_in_trial)
    },
    getEnterpriseStatus() {
      return (this.company[0]?.subscription?.type !== 'Enterprise') || (this.company[0]?.subscription?.type === 'Enterprise' && this.company[0]?.subscription?.status === 'inactive')
    },
    isOwnerOperator() {
      return this.$store.state?.user?.user?.team_member?.role === 'Owner Operator' || localStorage.getItem("role") === 'Owner Operator';
    }
  },
  methods: {
    ...mapActions(["getUserByID", "getLicenseInventory", "getCompany", "getUserCompanies", "getUserCompany", "getAppVersion", "getAllDocTypes", 'getAllDivisions', 'getRolePermissions', 'getUserPreferences', 'getSubscriptionDetails', 'logoutUser', 'setSidebarCollapsed']),
    async getSubscriptionDetailsUrl() {
      try {
        const url = await this.getSubscriptionDetails();
        if (url) window.location.href = url
      }
      catch (e) {
        console.log(e)
      }
    },
    getState(item) {
      this.openSidebar = item;
    },
    onResize() {
      this.isMobile = window.innerWidth <= 767;
    },
    async getAccountExpiryStatus() {
      const currentDate = moment.utc()
      const paymentDate = this.company[0]?.subscription.trial_end || this.company[0]?.trial_ends_at
      const expiryDate = moment.utc(paymentDate);
      const diffDays = moment.duration(expiryDate.diff(currentDate));
      const totalDays = Math.round(diffDays.asDays())
      if (totalDays > 0 && totalDays <= 15) {
        this.expiryWarning = Math.floor(totalDays)
      }
      if (this.company[0]?.banners?.show_enterprise_banner || this.company[0]?.banners?.show_stripe_banner) {
        this.showAccountExpiry = true
        await this.$nextTick()
        this.$bvModal.show('modal-account-expiry')
      }
    }
  },
  watch: {
    isOwnerOperator: {
      async handler(newVal) {
        if (newVal) {
          await this.$nextTick();
          this.$bvModal.show("modal-owner-operator-welcome");
        }
      },
      immediate: true
    },
  },
  async mounted() {
    window.addEventListener('resize', this.onResize);
    // In the Geotab add-in on desktop, show the collapsed icon rail by default.
    // On mobile, Geotab behaves like the normal mobile view (hidden by default).
    if (this.isGeotabAddin && !this.isMobile) {
      this.setSidebarCollapsed(true);
    }
    // Safety net for an already-logged-in session: if opened inside the Geotab
    // add-in and the cached session belongs to a different Geotab database, log
    // out so the user re-authenticates as the active org.
    if (this.isGeotabAddin) {
      try {
        const session = await getGeotabSession(3000);
        if (session && session.database && localStorage.getItem("geoTabDb") !== session.database) {
          await this.logoutUser();
          this.$router.push(`/login${this.isGeotabAddin ? '?geotabAddin=true' : ''}`);
          return;
        }
      } catch (e) {
        // Bridge unavailable / timed out — don't force logout on a transient failure.
      }
    }
    this.showLoader = true
    try {
      this.getUserCompany()
      if (localStorage.getItem("userSelectedCompany")) {
        await this.getCompany()
        await this.getRolePermissions()
        await this.getUserPreferences()
        this.getAccountExpiryStatus()

      }
      else {
        this.$router.push("/all-companies")
      }
      await this.getUserByID()
      await this.getUserCompanies()
      await this.getAppVersion()
      if (this.rolePermissions && this.rolePermissions['Doc Types'] && this.rolePermissions['Doc Types'][0].allow) await this.getAllDocTypes()
      if (this.rolePermissions && this.rolePermissions['Division'] && this.rolePermissions['Division'][0].allow) await this.getAllDivisions()
      if(!process.env.VUE_APP_SHOW_INTERCOM && this.user && this.company?.length > 0) {
        // Initialize Gleap with user data to prevent Guest sessions
        initGleapWithUser(this.user, this.company?.[0] || null)
      }
      this.showLoader = false
    } catch (error) {
      console.log(error);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Poppins&display=swap");

.expiry-data .btn .b-icon.bi,
.table_row .btn .b-icon.bi,
.add_license .btn .b-icon.bi {
  font-size: 150% !important;
}

* {
  font-family: poppins;
}

.text-black {
  color: #000 !important;
}

.o-1 {
  opacity: 1 !important;
}

.o-5 {
  opacity: 0.5 !important;
}

.h-24px {
  height: 24px !important;
}

.h-40px {
  height: 40px !important;
}

.min-width-auto {
  min-width: auto !important;
}

.h-350px {
  height: 350px;
}

.overflow-y-auto {
  overflow-y: auto;
}

.w-30px {
  width: 30px !important;
}
.mh-40px{
    min-height: 40px !important;
}
.w-35px {
  width: 35px !important;
}
.pl-0-3-5{
  padding-left: 0.35rem !important;
}

.w-40px {
  width: 40px !important;
}

.w-250px{
  width: 250px !important;
}

.w-220px{
  width: 220px !important;
}
.overflow-y-auto {
  overflow-y: auto;
}

.mw-40px {
  min-width: 40px !important;
}

.h-30px {
  height: 30px !important;
}

.h-35px {
  height: 35px !important;
}

.mw-30px {
  min-width: 30px !important;
}

.mw-35px {
  min-width: 35px !important;
}

.mh-30px {
  min-height: 30px !important;
}

.mh-35px {
  min-height: 35px !important;
}

.w-200px {
  width: 200px;
}

.mw-200px {
  min-width: 200px;
}

.maxw-200px {
  max-width: 200px;
}

.maxw-300px {
  max-width: 300px;
}

.w-250 {
  width: 250px !important;
}
.min-w-100 {
  min-width: 100px !important;
}
.min-w-260 {
  min-width: 260px !important;
}
.ml-10px{
  margin-left: 10px;
}
.w-300px {
  width: 300px !important;
}

.w-350px {
  width: 350px !important;
}

.mw-350px {
  max-width: 350px !important;
}

.object-fit-cover {
  object-fit: cover;
}

.top-0 {
  top: 0 !important;
}
.b-t-0{
  border-top: 0px !important;
}
.-m-16px{
  margin:0px -16px;
}
.btn-warning {
  background-color: #FFC800 !important;
  border-color: #FFC800 !important;
  color: white !important;
}

body.modal-open {
  overflow: visible;
}

.main-container {
  padding: 0 32px;
}

.w-available {
  width: -webkit-fill-available;
}

#wrapper,
.pt-64 {
  padding-top: 64px;
}

.mt-1-8rem {
  margin-top: 1.8rem;
}

.mt-6rem {
  margin-top: 6rem;
}
.-ml-3px{
  margin-left: -3px;
}

.bg-white-2 {
  background: #fff !important;
}

.custom-switch {
  padding-left: 0.7rem !important;
}

.pl-3-5 {
  padding-left: 1.2rem !important;
}
.pl-0 .custom-checkbox{
  padding-left: 0px !important;
}
.-r-8px {
  right: -8px !important;
}

.mr-2rem {
  margin-right: 2rem;
}

.item-description {
  max-width: 230px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

p.field-error {
  color: red;
  font-size: 13px !important;
  height: 4px;
  margin: 0;
}

.sr-only {
  display: none;
}

/* .table-body {
  background-color: #e5e5e5;
} */
/* body {
    background-color: #e5e5e5 !important;
} */
.notification-badge {
  background-color: #2D69F6;
  width: 17px;
  height: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  color: white;
  border-radius: 50%;
  top: -8px;
  right: -5px;
  font-size: 10px;
  font-weight: 700;
}

.expiry-notification {
  border-left: 10px solid #FF0000;
  border-radius: .25rem;
  box-shadow: 0 4px 50px rgb(0 0 0/10%);
}

.expiry-notification>p {
  font-weight: 500;
}

.data-table h4 {
  font-weight: 600;
  color: var(--black);
  font-size: 18px;
}

.label {
  color: var(--black);
  font-size: var(--extrasmall);
}

.label span {
  opacity: 0.6;
}

.expiry-data h2 {
  font-size: 32px;
  font-weight: 700;
  color: var(--black);
  margin-bottom: 0px;
}

.export {
  background-color: #2cce89 !important;
  font-size: 14px !important;
  width: 100px !important;
  height: 40px;
}

.export.table_btn {
  width: 100%;
}

.px-20px {
  padding-left: 20px !important;
  padding-right: 20px !important;
}

.col-form-label {
  color: #000000;
  opacity: 0.6;
}

.page-item .page-link {
  height: 29px;
  width: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-item.active .page-link {
  background-color: var(--btn-bg-color) !important;
  color: #fff;
}

.shadow-none {
  box-shadow: none !important;
}

.page-link {
  background-color: transparent !important;
  border-radius: 4px !important;
  border: none !important;
  font-size: 20px !important;
  padding-top: 4px !important;
}

.show-entries .custom-select {
  border: none !important;
  font-size: 14px !important;
}
.custom-select-2{
  padding: 0.375rem 2.75rem 0.375rem 0.75rem !important;
}

.page-item:not(.active) .page-link {
  color: black !important;
}

.table> :not(caption)>*>* {
  padding: 10px 15px;
}

.table.b-table>thead>tr>[aria-sort="none"] {
  border-bottom: none !important;
}

.table>tbody>tr>td {
  border-bottom: 1px solid #dee2e6;
}

.table.b-table>thead>tr>[aria-sort="none"],
.table.b-table>tfoot>tr>[aria-sort="none"] {
  background-image: url("../assets/up-and-down.svg") !important;
  color: var(--black);
  opacity: 0.6;
}

.table.b-table>thead>tr>[aria-sort="descending"],
.table.b-table>tfoot>tr>[aria-sort="descending"] {
  background-image: url("../assets/up-and-down.svg") !important;
  color: var(--black);
  opacity: 0.6;
}

.table.b-table>thead>tr>[aria-sort="ascending"],
.table.b-table>tfoot>tr>[aria-sort="ascending"] {
  background-image: url("../assets/up-and-down.svg") !important;
  color: var(--black);
  opacity: 0.6;
}

th div {
  font-weight: 400;
  font-size: var(--small);
  text-align: left;
}
th.text-right div{
  text-align: right;
}

tr td {
  text-align: left;
}

.right-0 {
  right: 0 !important;
}

.-z-1 {
  z-index: -1 !important;
}

.z-100 {
  z-index: 100 !important;
}
.break-word {
  word-break: break-word;
}

.remove-file-link {
  position: relative;
  top: -10px;
}

.equipment-border fieldset {
  margin-right: 20px;
}

.equipment-border fieldset .col-form-label {
  opacity: 1;
  font-size: var(--small);
}

.left-input .label,
.right-input .label {
  margin-right: 20px;
}

.table_row .v-select {
  border: 1px solid #e3e3e3 !important;
}

.dropdown-toggle:focus {
  box-shadow: none !important;
}

.border-radius-custom {
  border-radius: 0.25rem;
}

.custom-box-shadow,
.data-table {
  box-shadow: 0px 4px 50px rgb(0 0 0 / 10%);
}

.table_row .vs--searchable .vs__dropdown-toggle {
  min-height: 40px;
  border: none !important;
}

.entries-pagination {
  text-align: right;
  background: #fafafa;
  border-radius: 0rem 0rem 0.25rem 0.25rem;
}

.table-responsive {
  margin-bottom: 0 !important;
}

.table td,
.table th {
  vertical-align: middle !important;
}
.align-top{
  vertical-align: top !important;
}
.entries-pagination p {
  font-size: 14px;
  color: #000000;
  margin-bottom: 19px;
  margin-top: 10px;
}

.show-entries .custom-select {
  background: #fff url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Ctitle%3Edown-arrow%3C%2Ftitle%3E%3Cg%20fill%3D%22%23000000%22%3E%3Cpath%20d%3D%22M10.293%2C3.293%2C6%2C7.586%2C1.707%2C3.293A1%2C1%2C0%2C0%2C0%2C.293%2C4.707l5%2C5a1%2C1%2C0%2C0%2C0%2C1.414%2C0l5-5a1%2C1%2C0%2C1%2C0-1.414-1.414Z%22%20fill%3D%22%23000000%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E") no-repeat right 0.75rem center/8px 10px;
  background-color: #fff !important;
  border: 1px solid rgba(94, 114, 228, 0.1);
}

.custom-select {
  background: #f5f5f5 url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Ctitle%3Edown-arrow%3C%2Ftitle%3E%3Cg%20fill%3D%22%23000000%22%3E%3Cpath%20d%3D%22M10.293%2C3.293%2C6%2C7.586%2C1.707%2C3.293A1%2C1%2C0%2C0%2C0%2C.293%2C4.707l5%2C5a1%2C1%2C0%2C0%2C0%2C1.414%2C0l5-5a1%2C1%2C0%2C1%2C0-1.414-1.414Z%22%20fill%3D%22%23000000%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E") no-repeat right 0.75rem center/8px 10px !important;
  color: #495057 !important;
}

.show-entries label {
  font-weight: 500;
  margin-bottom: 0;
  margin: 0 10px;
}

.show-entries .custom-select {
  width: 61px;
  height: 35px !important;
  /* padding-right: 0; */
  padding-top: 4px;
  text-align: center;
  border: 1px solid rgba(94, 114, 228, 0.1);
  border-radius: 5px;
  font-weight: 500;
}

.modal-body {
  padding: 25px !important;
}

.pt-3 {
  padding-top: 1rem !important;
}

.pt-2,
.py-2 {
  padding-top: .5rem !important;
}

.modal-custom-padding {
  padding: 0.5rem 1rem !important;
}

.px-3 {
  padding-left: 1rem !important;
  padding-right: 1rem !important;
}

.-mr-12px {
  margin-right: -12px;
}

.ml-0-6rem {
  margin-left: 0.6rem;
}

.ml-1px {
  margin-left: 1px;
}

.b-sidebar {
  width: 443px !important;
}
.sidebar-task .b-sidebar, .sidebar-tire-tracker .b-sidebar, .sidebar-recurring-schedule .b-sidebar{
    width: 600px !important;
}

.gap-3 {
  gap: 1rem !important;
}

.bb-feedback-button{
  z-index: 1000 !important;
}

.columns-sidebar {
  width: 500px !important
}

.b-sidebar>.b-sidebar-header {
  background-color: #fff !important;
  color: black !important;
  font-size: 20px !important;
  font-weight: 700;
  padding: 0.8rem 1.5rem !important;
  border-bottom: 1px solid #EAEAEA;
}

.bg-dark {
  background-color: #000 !important;
}

.bg-black {
  background-color: #000 !important;
}

.b-y-0 {
  border-top: 0px !important;
  border-bottom: 0px !important;
}

.b-sidebar-backdrop {
  opacity: 0.8 !important;
}

.b-sidebar>.b-sidebar-body {
  background-color: white;
}

#modal-geo-tab-activate .modal-md .modal-content .modal-body,
#modal-isaac-activate .modal-md .modal-content .modal-body,
#modal-ps-activate .modal-md .modal-content .modal-body {
  padding: 25px 25px 0px 25px !important;
}

.dropdown-item:hover {
  background-color: #F6F7F9 !important;
}
.dropdown-item{
  white-space: break-spaces !important;
}

.show-entries {
  position: relative;
  padding: 5px 15px 5px 0px !important;
}

.show-entries svg {
  position: absolute;
  right: 65px;
  bottom: 5px;
}

.custom-select:focus {
  border-color: rgba(94, 114, 228, 0.1);
}

.pagination.b-pagination {
  margin-bottom: 0;
}

.pagination .page-item:last-child .page-link {
  margin-right: 0;
}

textarea.form-control::placeholder {
  color: #e3e3e3 !important;
}

input::placeholder {
  color: #e3e3e3 !important;
}

.b-form-btn-label-control.form-control>.form-control {
  color: #212529 !important;
}

.date-picker-custom {
  width: 100% !important;
}

.date-picker-custom .mx-input-wrapper>input {
  background-color: #fafafa !important;
  border: 1px solid #e3e3e3 !important;
  height: 40px;
  font-size: 12px;
  box-shadow: none !important;
}

.mx-icon-calendar {
  color: #212529 !important;
}

.b-table-sticky-header>.table.b-table>thead>tr>th {
  position: sticky;
  top: -1px !important;
}

#modal-set-password .modal-md,
#send-invite-modal .modal-md,
#tag-modal .modal-md,
#webhook-modal .modal-md,
#modal-password .modal-md,
#modal-geo-tab-activate .modal-md,
#modal-isaac-activate .modal-md,
#deleteModal .modal-md,
#change-password .modal-md,
#modal-schedule .modal-md,
#modal-schedule-complete .modal-md,
#modal-contact .modal-md,
#modal-driver-public-share .modal-md,
#modal-add-bulk-tags .modal-md,
#modal-ps-activate .modal-md,
#add-type-modal .modal-md,
#modal-bulk-doc-type .modal-md,
#modal-reject-doc .modal-md,
#modal-teammember .modal-md,
#contact-modal .modal-md,
#modal-division .modal-md,
#add-role-modal .modal-md,
#modal-bulk-expiry-date-update .modal-md,
#send-notification-modal .modal-md,
#modal-set-password-default-drivers .modal-md,
#modal-bulk-description-update .modal-md,
#modal-bulk-dot-mode-update .modal-md,
#modal-bulk-division-update .modal-md,
#modal-lock-trailer-vehicle .modal-md,
#modal-filter .modal-md,
#modal-filter-filedrop .modal-md,
#modal-accident-vehicle .modal-md,
#modal-cost .modal-md,
#type-modal .modal-md,
#form-modal .modal-md,
#notification-modal .modal-md,
#modal-vendor .modal-md,
#modal-company-id .modal-md,
#add-referral .modal-md,
#modal-sparkle-integration .modal-md,
#modal-optima-integration .modal-md,
#samsara-tile-modal .modal-md,
#samsara-invite-drivers-modal .modal-md,
#qr-password-modal .modal-md,
#modal-add-manifest .modal-md,
#modal-job-record .modal-md,
#modal-send-app .modal-md,
#modal-bulk-assign-update .modal-md,
#modal-bulk-due-date-update .modal-md,
#add-update-doc-type-modal .modal-md,
#custom-field-modal .modal-md,
#modal-add-table-view .modal-md,
#modal-line-item .modal-md,
#modal-import-ai .modal-md,
#modal-update-ai .modal-md,
#modal-import-inspection-ai .modal-md,
#modal-update-inspection-ai .modal-md,
#modal-violation .modal-md,
#modal-import-document-ai .modal-md,
#modal-add-dvir .modal-md,
#modal-add-dvir-item .modal-md,
#modal-edit-template-name .modal-md,
#modal-select-units .modal-md,
#modal-add-dvir-record .modal-md,
#modal-start-form .modal-md,
#modal-edit-answers .modal-md,
#modal-reassign-form .modal-md,
#modal-select-access .modal-md,
#modal-review-form .modal-md,
#modal-edit-form-name .modal-md,
#modal-bulk-due-date .modal-md
{
  width: 438px !important
}

#modal-driver-view .modal-md {
  width: 375px !important
}

#modal-referral .modal-lg,
#modal-owner-operator-welcome .modal-lg {
  width: 600px !important
}

#modal-geo-tab-activate .modal-md .modal-content .modal-header,
#modal-isaac-activate .modal-md .modal-content .modal-header,
#modal-ps-activate .modal-md .modal-content .modal-header {
  padding: 0px !important;
}

#help-modal .modal-md,
#modal-account-expiry .modal-md,
#modal-load-sample-data .modal-md {
  width: 598px !important
}


.show-password-section .b-icon {
  top: 12px !important
}

.profile_btn .save {
  background-color: var(--btn-bg-color) !important;
  width: 112px;
}

.resize-none {
  resize: none;
}

.sidebar-b-margin,
.mb-7rem {
  margin-bottom: 7rem;
}

.mb-6rem {
  margin-bottom: 6rem;
}
.v-tooltip__content{
  text-align: left !important;
}
.profile_btn .close,
.profile_btn .close:hover {
  width: 100px;
  height: 40px;
  background: #dddddd !important;
  border: 1px solid #dddddd !important;
  color: #000;
  opacity: 1;
}

.profile_btn .close:active,
.profile_btn .close:focus {
  color: #000;
  background: #dddddd !important;
}

.filter-box>div>fieldset {
  min-width: 185px !important;
}
.report-fields {
    max-width: max-content;
}
.filter-report-box>div>fieldset {
  width: 132px !important;
}
.w-fit-content,
.filter-box>div .report-datepicker {
  width: fit-content !important;
}
.w-max-content{
  width: max-content !important;
}
.filter-box>div .report-datepicker.w-250px{
  width: 250px !important;
}

.entries-pagination>div>div {
  margin-left: 1rem;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  background: white !important;
  z-index: 5;
}

.page-item>button[aria-checked="false"],
.page-item>button[aria-checked="true"],
.page-item>button[aria-label="Go to last page"],
.page-item>button[aria-label="Go to first page"],
.page-item>span[aria-label="Go to first page"],
.page-item.bv-d-xs-down-none,
.page-item>span[aria-label="Go to last page"] {
  display: none !important;
}

.bg-grey-light {
  background-color: #fafafa;
}

.text-grey-200 {
  color: #6B7280;
}
.text-grey-2{
  color:#797979;
}

.table thead th {
  opacity: 1 !important;
  background-color: #FAFAFA !important;
}
.table thead th > div{
  font-weight: 700 !important;
  opacity: 1
}
.font-table-weight-bold>div {
  font-weight: 700 !important;
  opacity: 1
}

.table-custom-border {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem !important;
}

.table-custom-border .card-body {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #fafafa !important;
}

.text_primary {
  color: var(--btn-bg-color) !important;
}

.fill-primary{
  fill: var(--btn-bg-color) !important;
}

.btn-primary-status {
  background-color: rgba(45, 105, 246, 0.1) !important;
  color: rgba(45, 105, 246, 1) !important;
  border: 1px solid rgb(45, 105, 246) !important;
  width: 110px !important;
  height: 30px !important;
}

.btn-secondary-status {
  background-color: rgba(133, 144, 158, 0.1) !important;
  color: rgba(133, 144, 158, 1) !important;
  border: 1px solid rgba(133, 144, 158) !important;
  width: 110px !important;
  height: 30px !important;
}

.btn-success-status {
  background-color: rgba(44, 206, 137, 0.1) !important;
  color: rgba(44, 206, 137, 1) !important;
  border: 1px solid rgb(44, 206, 137) !important;
  width: 110px !important;
  height: 30px !important;
}

.btn-warning-status {
  background-color: rgba(255, 200, 0, 0.1) !important;
  color: rgba(255, 200, 0, 1) !important;
  border: 1px solid rgb(255, 200, 0) !important;
  width: 110px !important;
  height: 30px !important;
}

.btn-danger-status {
  background-color: rgba(255, 0, 0, 0.1) !important;
  color: rgba(255, 0, 0, 1) !important;
  border: 1px solid rgb(255, 0, 0) !important;
  width: 110px !important;
  height: 30px !important;
}

.cancel_btn {
  font-weight: 600;
}

.text-pre-line {
  white-space: pre-line !important;
}

.zoom-section {
  position: sticky;
  left: 0px;
  right: 15px;
  bottom: 0;
  background: #323639;
  padding: 10px;
}

.h-240px {
  height: 240px !important;
}

.h-550px {
  height: 550px;
}

@media (min-width: 992px) {

  .pr-lg-0,
  .px-lg-0 {
    padding-right: 0 !important;
  }

  .pl-lg-0,
  .px-lg-0 {
    padding-left: 0 !important;
  }

  .pl-lg-3 {
    padding-left: 1rem !important;
  }

  .data-table fieldset:last-child {
    margin-right: 0;
  }

  .modal-lg,
  .modal-xl {
    max-width: 800px !important;
    width: 800px !important;
  }

  .mh-470px {
    min-height: 470px !important;
  }

  #modal-smart-drop-plus-preview .modal-lg,
  #modal-document .modal-lg,
  #modal-add-document .modal-lg,
  #modal-company-docs .modal-lg,
  #modal-company-ai-docs .modal-lg,
  #modal-bulk-equipment-docs .modal-lg,
  #modal-file-drop-document .modal-lg
  {
    max-width: 900px !important;
    width: 900px !important;
  }
  #modal-job-record .modal-lg{
    max-width: 600px !important;
    width: 600px !important;
  }
  #modal-company-ai-docs .modal-lg .modal-content {
    min-height: 650px;
  }
}

.table-striped>tbody>tr:nth-of-type(odd) {
  background: rgba(250, 250, 250, 0.8) !important
}

.table-hover>tbody>tr:hover,
.vs__dropdown-menu>li:hover,
.vs__dropdown-option--highlight {
  background-image: linear-gradient(rgba(0, 0, 0, 0.075), rgba(0, 0, 0, 0.075));
  background-repeat: no-repeat;
  background-color: transparent !important;
}

.vs__dropdown-menu>li:hover,
.vs__dropdown-option--highlight {
  color: #000 !important;
}

.table-striped.table-hover>tbody>tr:hover>td,
.table-striped.table-hover>tbody>tr:hover>th {
  --bs-table-accent-bg: #f3f5ff !important;
}

fieldset .col-form-label {
  text-transform: capitalize;
}

.cursor-pointer {
  cursor: pointer;
}

.mx-calendar-content .cell {
  text-align: center !important;
}

.change-status-btn {
  width: 110px;
  height: 40px;
  font-size: 14px !important;
  font-weight: 500 !important;
}

.z-1 {
  z-index: 1 !important;
}

.modal-footer{
  flex-wrap: nowrap !important;
}

.b-sidebar.b-sidebar-right {
  border: 1px solid #EAEAEA;
}

.table-tags {
  background-color: rgba(45, 105, 246, 0.1) !important;
  border: 1px solid rgba(45, 105, 246, 0.5) !important;
  color: #2D69F6 !important;
}

.table-tags {
  border-radius: 4px;
  margin: 4px 2px 0;
  padding: 0 .25em;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* .vs__selected .vs__deselect svg > path{
  fill: rgba(45, 105, 246,0.5) !important;
} */
.vs__selected,
.vs__search {
  height: 32px !important;
}

/* `append-to-body` renders the menu outside the component, so it can't inherit
   the f-12 on the field — match that size here instead. */
.vs__dropdown-menu {
  font-size: 12px;
}

.h-auto,
.h-auto>div {
  height: auto !important;
}

.vs__open-indicator,
.vs__clear {
  fill: black !important;
  transform: scale(0.6) !important;
}

.h-50px {
  height: 50px !important;
}

.w-4 {
  width: 16px;
}

.h-4 {
  height: 16px;
}

.h-90vh {
  height: 90vh !important;
}

.opacity-50 {
  opacity: 0.5 !important;
}

.overflow-lg-hidden,
.overflow-x-hidden {
  overflow: hidden !important;
}

.min-h-575px {
  min-height: 575px !important;
}

.user-notification-image {
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  background-color: #2D69F6;
  border: 2px solid #FAFAFA;
  color: white;
  display: flex;
  font-weight: 700;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 12px;
}

.notifications-count {
  font-size: 10px;
  font-weight: 700;
  width: 17px;
  height: 17px;
  background: #2D69F6;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: white;
  position: absolute;
  top: -5px;
  right: -5px;
}

.notification-img {
  border: 2px solid #FAFAFA;
}

.border-notification-section {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.notification-section {
  justify-content: space-between;
}

.notification-section .remarks {
  width: 350px;
  max-width: 350px;
}

@media (max-width: 575px) {
  .notification-section .remarks {
    width: auto;
  }
}

@media (min-width: 576px) {
  .notification-company-section {
    width: 180px;
  }
}

.user-notification-image {
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  background-color: #2D69F6;
  border: 2px solid #FAFAFA;
  color: white;
  display: flex;
  font-weight: 700;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 12px;
}

.notifications-count {
  font-size: 10px;
  font-weight: 700;
  width: 17px;
  height: 17px;
  background: #2D69F6;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: white;
  position: absolute;
  top: -5px;
  right: -5px;
}

.notification-img {
  border: 2px solid #FAFAFA;
}

.border-notification-section {
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.notification-section {
  justify-content: space-between;
}

.notification-section .remarks {
  width: 350px;
  max-width: 350px;
}

@media (max-width: 575px) {
  .notification-section .remarks {
    width: auto;
  }
}

@media (min-width: 576px) {
  .notification-company-section {
    width: 180px;
  }
}

@media (min-width: 1200px) {

  .pr-xl-3,
  .px-xl-3 {
    padding-right: 1rem !important;
  }

  .pl-xl-0,
  .px-xl-0 {
    padding-left: 0 !important;
  }

  .pr-xl-0,
  .px-xl-0 {
    padding-right: 0 !important;

  }
}

@media (max-width: 991px) {
  .overflow-lg-hidden {
    overflow: auto !important;
  }

  .data-table {
    overflow-x: scroll;
  }

  .zoom-section {
    left: 15px;
  }
}

@media (max-width: 767px) {
  /* .expiry-data {
    display: block !important;
  } */

  .expiry-data h2 {
    font-size: 26px;
  }

  .expiry-data .export {
    width: fit-content;
  }

  #page-content-wrapper {
    padding-left: 76px;
  }

  .main-container {
    padding: 0 10px 0px 0px !important;
  }
}

.expiry-warning-section {
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

@media (max-width: 700px) {
  .b-sidebar {
    width: 100% !important;
  }
}

@media (min-width: 768px) {

  .mr-md-3,
  .mx-md-3 {
    margin-right: 1rem !important;
  }

  .pl-md-0,
  .px-md-0 {
    padding-left: 0 !important;
  }

  .ml-md-0,
  .mx-md-0 {
    margin-left: 0 !important;
  }
}

@media (min-width: 575px) {
  .expiry-warning-section {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .pl-sm-2,
  .px-sm-2 {
    padding-left: .5rem !important;
  }

  .pr-sm-2,
  .px-sm-2 {
    padding-right: .5rem !important;
  }
}

@media (max-width: 575px) {
  .notification-dropdown .dropdown-menu {
    transform: translate3d(-215px, 24px, 0px) !important;
    width: 325px;
  }

  .modal-footer{
  flex-wrap: wrap !important;
}
.modal-footer .btn {
 font-size: 12px !important;
}
  .notification-details-section {
    width: 215px;
  }

  #dropdown-right button {
    padding-right: 0px !important;
  }

  */ .expiry-data h2 {
    font-size: 20px;
  }

  .export p {
    font-size: 12px;
  }

  .table-body {
    width: 100% !important;
    margin-left: auto;
  }

  #page-content-wrapper {
    padding-left: 0;
  }

  .profile_btn {
    flex-wrap: wrap;
    justify-content: flex-start !important;
  }

  .filter-box>div .report-fields,
  .filter-box>div .report-datepicker {
    width: 100% !important;
    margin-right: 0px !important;
    font-size: 12px !important;
  }

  .filter-box>div .report-datepicker>div .mx-datepicker-range {
    width: 100% !important;
  }
}

@media (max-width: 425px) {

  #help-modal .modal-md,
  #modal-set-password .modal-md,
  #send-invite-modal .modal-md,
  #tag-modal .modal-md,
  #webhook-modal .modal-md,
  #modal-password .modal-md,
  #modal-geo-tab-activate .modal-md,
  #modal-isaac-activate .modal-md,
  #deleteModal .modal-md,
  #change-password .modal-md,
  #modal-schedule .modal-md,
  #modal-schedule-complete .modal-md,
  #modal-contact .modal-md,
  #modal-driver-public-share .modal-md,
  #modal-add-bulk-tags .modal-md,
  #modal-ps-activate .modal-md,
  #add-type-modal .modal-md,
  #modal-bulk-doc-type .modal-md,
  #modal-reject-doc .modal-md,
  #modal-teammember .modal-md,
  #contact-modal .modal-md,
  #modal-division .modal-md,
  #add-role-modal .modal-md,
  #modal-bulk-expiry-date-update .modal-md,
  #send-notification-modal .modal-md,
  #modal-set-password-default-drivers .modal-md,
  #modal-load-sample-data .modal-md,
  #modal-bulk-description-update .modal-md,
  #modal-bulk-dot-mode-update .modal-md,
  #modal-bulk-division-update .modal-md,
  #modal-lock-trailer-vehicle .modal-md,
  #modal-filter .modal-md,
  #modal-filter-filedrop .modal-md,
  #modal-accident-vehicle .modal-md,
  #modal-cost .modal-md,
  #type-modal .modal-md,
  #modal-vendor .modal-md,
  #modal-driver-view .modal-md,
  #form-modal .modal-md,
  #notification-modal .modal-md,
  #modal-account-expiry .modal-md,
  #modal-company-id .modal-md,
  #modal-referral .modal-lg,
  #add-referral .modal-md,
  #modal-sparkle-integration .modal-md,
  #modal-optima-integration .modal-md,
  #samsara-tile-modal .modal-md,
  #samsara-invite-drivers-modal .modal-md,
  #qr-password-modal .modal-md,
  #modal-owner-operator-welcome .modal-lg ,
  #modal-add-manifest .modal-md,
  #modal-job-record .modal-md,
  #modal-send-app .modal-md,
  #modal-bulk-assign-update .modal-md ,
  #modal-bulk-due-date-update .modal-md,
  #modal-job-record .modal-lg,
  #add-update-doc-type-modal .modal-md,
  #custom-field-modal .modal-md,
  #modal-add-table-view .modal-md,
  #modal-line-item .modal-md,
  #modal-import-ai .modal-md,
  #modal-update-ai .modal-md,
  #modal-import-inspection-ai .modal-md,
  #modal-update-inspection-ai .modal-md,
  #modal-violation .modal-md,
  #modal-import-document-ai .modal-md,
  #modal-add-dvir .modal-md,
  #modal-add-dvir-item .modal-md,
  #modal-edit-template-name .modal-md,
  #modal-select-units .modal-md,
  #modal-add-dvir-record .modal-md,
  #modal-start-form .modal-md,
  #modal-edit-answers .modal-md,
  #modal-reassign-form .modal-md,
  #modal-select-access .modal-md,
  #modal-review-form .modal-md,
  #modal-edit-form-name .modal-md,
  #modal-bulk-due-date .modal-md
  {
    width: 100% !important;
  }
}

@media (max-width: 400px) {
  .b-sidebar>.b-sidebar-header {
    padding: 0.5rem !important;
  }
}</style>
